import { useSearchFilm } from "@/actions/useMovies";
import { SearchIcon, XCircleIcon } from "@heroicons/react/outline";
import React, { useContext, useEffect } from "react";
import { useRef } from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import * as Text from "@/components/text";
import Image from "next/legacy/image";
import { router } from "next/router";
import { FilterContext } from "stores/FilterContextProvider";
import {
  IniciativaItemContext,
  IniciativaItemDispatchContext,
  setKeyword,
} from "stores/IniciativasContextProvider";

const pVariant = {
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const cVariant = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export function Skeletons() {
  return (
    <div className="flex w-full animate-pulse flex-row items-center gap-2 rounded border border-gray-200 p-1 shadow dark:border-gray-700 md:p-2 md:pl-10">
      <svg
        className="h-12 w-12 text-gray-200 dark:text-gray-600"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 640 512"
      >
        <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z" />
      </svg>

      <div>
        <div className="mb-2 h-2.5 w-32 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2 w-48 rounded-full bg-gray-200 dark:bg-gray-700"></div>
      </div>
    </div>
  );
}

export function Badge({ word }) {
  return (
    <div className="flex items-center justify-center gap-4">
      <p className="text-xl text-neutral-500">{word}</p>
      <XCircleIcon
        width={16}
        height={16}
        className="mt-[2px] text-neutral-700 hover:text-neutral-800"
      />
    </div>
  );
}

const useBounceBack = (value, delay, dispatch, setKeyword, initialValue) => {
  const [bounce, setBounce] = useState(value);
  useEffect(() => {
    if (initialValue.current) return;
    const bounceOut = setTimeout(() => {
      setBounce(value);
      setKeyword(dispatch, value);
    }, delay);
    return () => clearTimeout(bounceOut);
  }, [value]);
  return bounce;
};

export default function SearchBarCatalog({
  standAlone = true,
  badge,
  hidePreview = true,
  delaySearch = 500,
}) {
  const [value, setBounce] = useState("");
  const [search, setSearch] = useState("");
  const [badgeWord, setBadgeWord] = useState(value);
  const inputRef = useRef(null);
  const iValue = useRef(true);
  const { data, isLoading } = useSearchFilm(search);
  const dispatch = useContext(IniciativaItemDispatchContext);
  const { selectedItem, keyword } = useContext(IniciativaItemContext);
  const bounceValue = useBounceBack(
    value,
    delaySearch,
    dispatch,
    setKeyword,
    iValue
  );
  const filters = useContext(FilterContext);

  useEffect(() => {
    document.addEventListener("keydown", handleEnter);
    return () => {
      document.removeEventListener("keydown", handleEnter);
    };
  }, []);

  useEffect(() => {
    if (keyword !== "") {
      iValue.current = true;
    } else {
      iValue.current = false;
    }
  });

  // useEffect(() => {
  //   setBadgeWord(badge);
  // }, [badge]);

  const cancelSearch = () => {
    setSearch("");
    setBounce("");
    setKeyword(dispatch, "");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const getActiveQuery = () => {
    return {
      keyword: inputRef.current ? inputRef.current.value : keyword,
    };
  };

  const handleEnter = (e) => {
    if (e.key !== "Enter") return;
    router.push({
      pathname: "/catalogo",
      query: getActiveQuery(),
    });
  };

  const handlePress = (e) => {
    router.push({
      pathname: "/catalogo",
      query: getActiveQuery(),
    });
    cancelSearch();
  };

  const setImg = (attributes) => {
    if (attributes.film_cover_image.data) {
      return attributes.film_cover_image.data.attributes.formats.thumbnail.url;
    }
    return "";
  };

  const showResults = (list) => {
    return list.map(({ id, attributes }, i) => {
      return (
        <>
          <motion.a
            href={`/catalogo/${attributes.filmSlug}`}
            variants={cVariant}
            layout
            key={id}
            className="flex w-full items-center justify-start gap-4 sm:px-3 sm:py-1"
          >
            <div className="h-12 w-14 sm:pt-1">
              <Image
                width={48}
                height={48}
                layout="fixed"
                objectFit="cover"
                src={setImg(attributes)}
                objectPosition="center"
                alt="Thumbnail"
              />
            </div>

            <div className="h-full w-full border-b-[1px] border-b-neutral-300/50 sm:py-2">
              <Text.Body className={"text-sm font-semibold text-neutral-800"}>
                &nbsp;
                {attributes.film_name}&nbsp;
              </Text.Body>
              <div>
                {attributes.Directores.map((d) => {
                  return (
                    <Text.Body
                      key={d.id}
                      className={"text-[0.8rem] text-neutral-600"}
                    >
                      &nbsp;{d.Director}&nbsp;
                    </Text.Body>
                  );
                })}
              </div>
            </div>
          </motion.a>
          {list.length - 1 === i ? (
            <button onClick={() => handlePress()} className="sm:p-3">
              &nbsp;Ver todos los resultados&nbsp;
            </button>
          ) : // </Link>
            null}
        </>
      );
    });
  };

  const handleFocus = () => {
    if (badgeWord) {
      setBadgeWord(null);
    }
  };

  const handleOnChange = (e) => {
    const film = e;
    iValue.current = false;
    setBounce(film);
  };

  useEffect(() => {
    if (keyword === "") {
      setSearch("");
    } else {
      setSearch(keyword);
    }
  }, [keyword]);

  return (
    <>
      <div className="relative">
        <div className="absolute inset-y-0 right-1 my-3 ml-3 flex items-center bg-white pl-1">
          {keyword !== "" ? (
            <div className="cursor-pointer" onClick={() => cancelSearch()}>
              <XCircleIcon className="w-8 text-neutral-600/50 hover:text-neutral-800" />
            </div>
          ) : (
            <SearchIcon
              className={"mr-2 w-5 text-neutral-600/50 hover:text-neutral-800"}
            />
          )}
        </div>

        {badgeWord ? (
          <div
            className="absolute left-4 top-2 cursor-pointer sm:top-[5px]"
            onClick={() => setBadgeWord(null)}
          >
            <Badge word={badgeWord} />
          </div>
        ) : null}

        {iValue.current && !standAlone ? (
          <input
            value={keyword}
            onChange={(e) => handleOnChange(e.target.value)}
            className="
          h-full w-full truncate text-ellipsis border border-gray-300 p-2 text-neutral-1000 sm:text-[12px] md:text-lg"
            placeholder="Busca peliculas por título o realizador/a"
          />
        ) : (
          <input
            onChange={(e) => handleOnChange(e.target.value)}
            ref={inputRef}
            onFocus={handleFocus}
            className="
          h-full w-full border border-gray-300 p-2 text-neutral-1000 sm:text-[12px] md:text-lg"
            placeholder={
              badgeWord ? "" : "Busca peliculas por título o realizador/a"
            }
          />
        )}

        {!hidePreview ? (
          data && !isLoading ? (
            <div className="relative w-full">
              {/* <BlockingOverlay
              onClick={() => cancelSearch()}
              transparent={true}
            /> */}
              <AnimatePresence>
                <motion.ul
                  animate="animate"
                  initial="initial"
                  exit="exit"
                  variants={pVariant}
                  layout
                  className="absolute z-10 w-full bg-white py-1 shadow-2xl shadow-black"
                >
                  {data.data.length > 0 ? (
                    showResults(data.data)
                  ) : (
                    <li className="w-full border-b-[1px] border-b-neutral-300/50 sm:px-3 sm:py-2">
                      <Text.Body
                        className={"text-sm font-semibold text-neutral-800"}
                      >
                        {`No existen películas que contengan "${bounceValue}"`}
                      </Text.Body>

                      <Text.Body className={"text-[0.8rem] text-neutral-600"}>
                        Intenta de nuevo con otro nombre
                      </Text.Body>
                    </li>
                  )}
                </motion.ul>
              </AnimatePresence>
            </div>
          ) : (
            bounceValue.length > 0 &&
            keyword !== "" && (
              <div className="relative w-full">
                <AnimatePresence>
                  <motion.ul
                    animate="animate"
                    initial="initial"
                    exit="exit"
                    variants={pVariant}
                    layout
                    className="absolute z-10 w-full bg-white py-1 shadow-2xl shadow-black"
                  >
                    <Skeletons />
                  </motion.ul>
                </AnimatePresence>
              </div>
            )
          )
        ) : null}
      </div>
    </>
  );
}
